<template>
    <div class="rounded-xl border border-gray-300 bg-gray-100 px-6 py-4">
        <div class="flex items-center gap-4">
            <h3 class="text-md font-semibold text-gray-900">{{ title }}</h3>

            <button
                type="button"
                class="ml-auto text-md font-semibold text-blue-600 transition hover:text-blue-700"
                @click="$emit('toggle-link')"
            >
                {{ toggleLinkText }}
            </button>
        </div>

        <div class="mt-6 space-y-6">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                    <label
                        :for="titleId"
                        class="mb-3 block text-sm font-medium text-gray-900"
                    >
                        Title
                    </label>
                    <input
                        :id="titleId"
                        v-model="localTitle"
                        type="text"
                        class="h-10 w-full rounded-md border border-gray-300 bg-white px-4 text-md text-gray-700 focus:border-blue-500 focus:outline-none"
                        placeholder="Placeholder"
                    />
                </div>
                <div>
                    <label
                        :for="slugId"
                        class="mb-3 block text-sm font-medium text-gray-900"
                    >
                        Slug
                    </label>
                    <input
                        :id="slugId"
                        v-model="localSlug"
                        type="text"
                        class="h-10 w-full rounded-md border border-gray-300 bg-white px-4 text-md text-gray-700 focus:border-blue-500 focus:outline-none"
                        placeholder="enter-slug"
                    />
                </div>
            </div>

            <div>
                <label
                    :for="`${titleId}_file`"
                    class="mb-3 block text-sm font-medium text-gray-900"
                >
                    {{ uploadLabel }}
                </label>
                <div
                    class="flex items-center gap-3 rounded-md border border-gray-300 bg-white px-3 py-2"
                >
                    <span
                        class="flex-1 truncate text-md text-gray-500"
                        aria-live="polite"
                    >
                        {{ localFile ? localFile.name : emptyFileText }}
                    </span>
                    <button
                        type="button"
                        class="rounded-md bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-300"
                        :aria-label="`Choose file for ${uploadLabel}`"
                        @click="openFilePicker"
                    >
                        Upload
                    </button>
                    <input
                        :id="`${titleId}_file`"
                        ref="fileInput"
                        type="file"
                        :accept="accept"
                        :aria-label="uploadLabel"
                        class="hidden"
                        @change="handleFileChange"
                    />
                </div>
            </div>
        </div>

        <div class="mt-10 flex justify-end gap-4">
            <button
                type="button"
                class="rounded-md bg-gray-200 px-6 py-2 text-md font-semibold text-gray-700 transition hover:bg-gray-300"
                :disabled="isLoading"
                @click="handleCancel"
            >
                Cancel
            </button>
            <button
                type="button"
                class="rounded-md px-6 py-2 text-md font-semibold text-white transition"
                :class="
                    canSubmit
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'cursor-not-allowed bg-blue-300'
                "
                :disabled="!canSubmit"
                @click="handleSave"
            >
                {{ isLoading ? "Uploading..." : saveButtonText }}
            </button>
        </div>
    </div>
</template>

<script lang="ts">
import axios from "axios";

export default {
    props: {
        title: {
            type: String,
            required: true,
        },
        type: {
            type: String,
            required: true,
        },
        toggleLinkText: {
            type: String,
            required: true,
        },
        uploadLabel: {
            type: String,
            required: true,
        },
        emptyFileText: {
            type: String,
            required: true,
        },
        accept: {
            type: String,
            required: true,
        },
        saveButtonText: {
            type: String,
            default: "Save",
        },
        titleId: {
            type: String,
            default: "new_resource_title",
        },
        slugId: {
            type: String,
            default: "new_resource_slug",
        },
        lessonId: {
            type: [String, Number],
            default: null,
        },
    },
    data(): any {
        return {
            localTitle: "",
            localSlug: "",
            localFile: null,
            isLoading: false,
        };
    },
    computed: {
        canSubmit(): boolean {
            return (
                !!this.localTitle &&
                !!this.localSlug &&
                !!this.localFile &&
                !this.isLoading
            );
        },
    },
    watch: {
        localSlug(newValue: string) {
            const converted = newValue
                .toLowerCase()
                .replace(/\s+/g, "-")
                .replace(/[^a-z0-9-]/g, "");
            if (converted !== newValue) {
                this.localSlug = converted;
            }
        },
    },
    methods: {
        openFilePicker() {
            const fileInput = this.$refs.fileInput as HTMLInputElement;
            fileInput?.click();
        },
        handleFileChange(event: Event) {
            const target = event.target as HTMLInputElement;
            this.localFile = target.files?.[0] ?? null;
        },
        handleCancel(shouldEmit = true) {
            this.localTitle = "";
            this.localSlug = "";
            this.localFile = null;

            const fileInput = this.$refs.fileInput as HTMLInputElement;
            if (fileInput) {
                fileInput.value = "";
            }

            if (shouldEmit) {
                this.$emit("cancel");
            }
        },
        handleSave() {
            if (!this.canSubmit) {
                return;
            }

            this.submitForm();
        },
        async submitForm() {
            this.isLoading = true;

            try {
                const formData = new FormData();
                formData.append("title", this.localTitle);
                formData.append("slug", this.localSlug);
                formData.append("type", this.type);
                formData.append("file", this.localFile);

                if (this.lessonId) {
                    formData.append("lesson_id", String(this.lessonId));
                }

                const response = await axios.post(
                    "/api/admin/content/learn/resources",
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    }
                );

                const data = response.data;

                this.$emit("create", {
                    id: data.id,
                    title: data.title,
                    slug: data.slug,
                    type: data.type,
                    file_path: data.file_path,
                });

                this.handleCancel(false);

                const successMessage =
                    data.message || "Resource upload has started.";

                this.$events.$emit("toastNotification", {
                    action: "upload",
                    heading: "Resource Uploaded",
                    status: "success",
                    message: successMessage,
                });
            } catch (error: any) {
                console.error("Error:", error);

                const errorMessage =
                    error.response?.data?.message ||
                    error.message ||
                    "Failed to create resource. Please try again.";

                this.$events.$emit("toastNotification", {
                    action: "upload",
                    heading: "Upload Failed",
                    status: "error",
                    message: errorMessage,
                });
            } finally {
                this.isLoading = false;
            }
        },
    },
};
</script>
